import { notifyError } from 'src/utilities/monitoring.js';
import { t } from 'src/i18n';
import { type ApiClient, signingAttachment } from 'src/store/backend';
import {
  contactsNotDeleted,
  currentUserIsOwner,
  getClassificationText,
  getOrganizationId,
  currentEmployeeUuid,
  currentUserUuid,
  employeesShortNamesAndI,
  functionsNotDeleted,
  getContactsAsUsers,
  getEmployeesById,
  employeesDeleted,
  contactsDeleted,
  partnersNotDeleted,
  partnersDeleted,
  currentContactUuid,
  suggestedReports,
} from 'src/store/selectors';
import {
  BASE_PATH,
  ContactPersonViewModelAccessLevelEnum,
  EmployeeViewModelStatusEnum,
} from '@trinnvis/dabih-api-client';
import { employeesNotDeleted, getOrganization, key, toReportViewModel } from 'src/store/selectors';
import type { State } from 'src/store/types';
import { LocalDate } from 'src/utilities/local-date';
import { dateTimeDescription, sortedByName } from 'src/store/utilities';
import { sortBy } from 'lodash';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { CommonDataEntityView } from 'src/content/common-data-entity-view';
import type { ReportView } from 'src/content/reports/report-view';
import type { ReportViewEditItem } from 'src/content/reports/report-view-edit-item';
import { assertIsDefined } from 'src/assertIsDefined';
import { EntityNotFoundError } from 'src/models/content/entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchRevisions } from '../fetch-revisions';
import { pagesAsSelectTagOptions } from '../pages-as-select-tag-options';
import { fetchDraft } from '../fetch-draft';
import { getStore } from 'src/store';
import { getFeatureStates } from 'src/store/selectors/features/getFeatureStates';
import { functionNameWithAssignedNames } from 'src/store/functionNameWithAssignedNames';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import { capitalize, fullName } from 'src/utilities/text';
import type { EntityOption } from 'src/layout/application-view-model';
import { extractFormData, getFormPrompt } from 'src/store/selectors/form-functions';
import { userNames, userNamesAndI } from 'src/store/selectors/user-names';
import { currentUserName } from 'src/store/selectors/user/currentUserName';
import type { FormElement } from 'src/library/components/form';
import { peopleAndFunctions } from 'src/store/selectors/assignees';
import { availableMeetingMessageRecipients, meetingParticipants } from 'src/store/selectors/meetings/meetings';

function availableFunctions(state: State): SelectDropdownOption[] {
  return sortBy(
    functionsNotDeleted(state)
      .filter((f) => {
        return f.isConfirmedEntity;
      })
      .map((f) => {
        return {
          value: f.uuid,
          text: functionNameWithAssignedNames(f.uuid, functionsNotDeleted(state), employeesShortNamesAndI(state)),
        };
      }),
    ['text'],
  );
}

function availableEmployees(state: State, uuids: string[] | undefined): SelectDropdownOption[] {
  return sortBy(
    employeesShortNamesAndI(state)
      .filter((e) => {
        if (uuids && uuids.includes(e.uuid)) {
          return true;
        }
        return e.name !== '' && e.status !== 'TERMINATED';
      })
      .map((p) => {
        let text = p.name;
        let disabled = false;
        if (getEmployeesById(state)[p.uuid].accessLevel === 'READONLY') {
          text += ' ' + t('reports.accessLevelReadonlySuffix');
          disabled = true;
        }
        if (getEmployeesById(state)[p.uuid].accessLevel === 'NONE') {
          text += ' ' + t('reports.accessLevelNoneSuffix');
          disabled = true;
        }
        return { value: p.uuid, text, disabled };
      }),
    ['text'],
  );
}

function availableContacts(state: State, uuids: string[] | undefined = undefined): SelectDropdownOption[] {
  return sortBy(
    getContactsAsUsers(state)
      .filter((p) => {
        if (uuids && uuids.includes(p.uuid)) {
          return true;
        }
        return p.name !== '' && p.accessLevel === 'USER';
      })
      .map((p) => ({ value: p.uuid, text: p.name + ' (' + p.partnerName + ')' })),
    ['text'],
  );
}

function availableEmployeesForAuthor(state: State, uuid: string | undefined) {
  const employees = employeesNotDeleted(state)
    .filter((e) => {
      return (
        e.uuid === uuid ||
        (e.accessLevel === 'USER' && e.status !== EmployeeViewModelStatusEnum.Terminated && e.isConfirmedEntity)
      );
    })
    .concat(
      employeesDeleted(state).filter((e) => {
        return e.uuid === uuid;
      }),
    )
    .map((e) => {
      return { value: e.uuid, text: e.name };
    });
  return sortBy(employees, 'text');
}

function availableContactsForAuthor(state: State, uuid: string | undefined) {
  const contacts = contactsNotDeleted(state)
    .filter((c) => {
      return c.uuid === uuid || (c.accessLevel === ContactPersonViewModelAccessLevelEnum.User && c.isConfirmedEntity);
    })
    .concat(
      contactsDeleted(state).filter((e) => {
        return e.uuid === uuid;
      }),
    )
    .map((c) => {
      let name = fullName(c.firstName, c.lastName);
      const partner = partnersNotDeleted(state)
        .concat(partnersDeleted(state))
        .find((p) => {
          return p.uuid === c.partnerUuid;
        });
      if (partner) {
        name += ' (' + partner.name + ')';
      }
      return { value: c.uuid, text: name };
    });
  return sortBy(contacts, 'text');
}

function isResponsible(state: State, item: ReportView): boolean {
  if (item.currentUserUuid) {
    if (item.formResponsibleFunction) {
      const responsibleFunction = functionsNotDeleted(state).find((f) => {
        return f.uuid === item.formResponsibleFunction;
      });
      if (responsibleFunction?.employees.includes(item.currentUserUuid)) {
        return true;
      }
    }
    if (item.formResponsibleEmployees?.includes(item.currentUserUuid)) {
      return true;
    }
    if (item.formResponsibleContacts?.includes(item.currentUserUuid)) {
      return true;
    }
  }
  return false;
}

function isRespondent(item: ReportView): boolean {
  if (item.currentUserUuid) {
    if (item.formRespondentEmployees) {
      if (item.formRespondentEmployees.includes(item.currentUserUuid)) {
        return true;
      }
    }
    if (item.formRespondentContacts) {
      if (item.formRespondentContacts.includes(item.currentUserUuid)) {
        return true;
      }
    }
  }
  return false;
}

function getContactsForShare(
  responsibleContacts: string[],
  respondentContacts: string[],
  contacts: SelectDropdownOption[],
  currentUserUuid: string,
): EntityOption[] {
  const result: EntityOption[] = [];
  responsibleContacts.concat(respondentContacts).forEach((uuid: string) => {
    const contact = contacts.find((c) => {
      return c.value === uuid;
    });
    if (
      result.find((r) => {
        return r.uuid === uuid;
      }) === undefined &&
      uuid !== currentUserUuid
    ) {
      result.push({ uuid: uuid, name: contact?.text ?? '', disabled: false });
    }
  });
  return result;
}

function getAuthor(
  employeeUuid: string | undefined,
  contactUud: string | undefined,
  currentEmployee: string | undefined,
  currentContact: string | undefined,
): string {
  if (employeeUuid) {
    return employeeUuid;
  }
  if (contactUud) {
    return contactUud;
  }
  if (currentEmployee) {
    return currentEmployee;
  }
  if (currentContact) {
    return currentContact;
  }
  return '';
}

export async function buildReportView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<ReportView> {
  const organization = getOrganization(state);
  assertIsDefined(organization, 'organization for buildReportView');
  const availablePersons = employeesNotDeleted(state)
    .filter((e) => e.status !== EmployeeViewModelStatusEnum.Terminated && e.isConfirmedEntity)
    .map((p) => ({ value: p.uuid, text: p.name }));
  const availablePages = pagesAsSelectTagOptions(state);
  const item = organization.reportsById[uuid];
  if (!item) {
    // Rapporten ligger ikke i state — slettet, feil organisasjon eller utdatert
    // lenke. Forventet brukeradferd (jf. 404), så vi kaster en typet feil som
    // content-view-factory degraderer til en tom visning + én gruppert warning.
    throw new EntityNotFoundError('reports', uuid);
  }
  const entity = toReportViewModel(item, organization);

  // Fetch revisions with graceful error handling
  let revisions;
  try {
    revisions = await fetchRevisions(
      api,
      'reports',
      uuid,
      organization.organizationId,
      entity.templateDeleted !== undefined && entity.templateDeleted !== '',
      undefined,
      entity.contentLastModified,
    );
  } catch (error) {
    // Log the error but don't fail the entire view building
    const responseError = error as any;
    const httpStatus = responseError?.status ?? responseError?.response?.status;
    console.warn(`Failed to fetch revisions for report ${uuid}:`, {
      uuid,
      httpStatus: httpStatus ?? 'unknown',
      errorMessage: error instanceof Error ? error.message : String(error),
      errorType: error?.constructor?.name ?? 'unknown',
    });
    // Return empty revisions array to degrade gracefully
    revisions = [];
  }

  const resultItem: ReportView = {
    ...commonData,
    promoData: getPromoDataForEntity('reports', item, getFeatureStates(state)),
    activeEmployeesCount: employeesNotDeleted(state).filter((e) => {
      return e.status === 'ACTIVE';
    }).length,
    currentUserIsOwner: currentUserIsOwner(state),
    hasSigningModule: false, // only if meeting report
    peopleAndFunctions: peopleAndFunctions(state),
    availablePartners: [],
    organizationName: organization.name,
    availableEmployees: sortedByName(employeesNotDeleted(state))
      .filter((e) => e.status !== 'TERMINATED' && e.isConfirmedEntity)
      .map((p) => ({
        uuid: p.uuid,
        email: p.email,
        personalIdentifier: p.personalIdentifier,
        firstName: p.firstName,
        lastName: p.lastName,
      })),
    availableContacts: [],
    signingOrders:
      entity.signingOrders?.map((s) => {
        return {
          ...s,
          asDocumentItem: async () => {
            const organizationId = getOrganizationId(getStore().getState());
            const a2 = await signingAttachment(api, 'reports', entity.uuid, organizationId, s.uuid);
            return {
              name: a2.name || t('text.withoutNameLowercase'),
              url: a2.urlForInline || '',
              urlForDownload: a2.url || '',
              entityType: 'contracts',
              attachmentId: s.uuid,
              uuid: entity.uuid,
            };
          },
        };
      }) || [],
    docsForLinking: [],
    contentLastModified: entity.contentLastModified ?? '',
    contentLastModifiedBy: entity.contentLastModifiedBy ?? '',
    contentLastModifiedWasCreate: entity.contentLastModifiedWasCreate ?? false,
    hasTemplateDelete: entity.hasTemplateDelete ?? false,
    hasTemplateUpdate: entity.hasTemplateUpdate ?? false,
    templateDeclined: entity.templateDeclined ?? '',
    templateDeleted: entity.templateDeleted ?? '',
    templateDeletedMessage: entity.templateDeletedMessage ?? '',
    templateUpdated: entity.templateUpdated ?? '',
    currentUserHasAccess:
      entity.classification === 'NONE' || (entity.accessControl ?? []).includes(currentUserUuid(state) ?? ''),
    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: entity.helpContent ?? '',
    href: currentParent + '/reports/' + uuid,
    pdfHref:
      BASE_PATH + '/organizations/' + organization.organizationId + '/reports/' + uuid + '.pdf?key=' + key(state),

    parentHref: currentParent,
    deleted: entity.deleted === true,
    type: 'reports',
    name: entity.name ?? '',
    classification: entity.classification ?? 'NONE',
    accessControl: entity.accessControl ?? [],
    classificationText: getClassificationText(
      entity.classification ?? 'NONE',
      entity.accessControl ?? [],
      commonData.accessControlOptions,
    ),
    revisions,
    attachments: toAttachmentItems(api, 'reports', entity.uuid, entity.attachments),
    reportDate: entity.reportDate ?? LocalDate.now().toString(),
    writtenByName: availablePersons.find((x) => x.value === entity.employeeUuid)?.text ?? '',
    availablePages: availablePages,
    availableEmployeesForAuthor: availableEmployeesForAuthor(state, entity.employeeUuid),
    availableContactsForAuthor: availableContactsForAuthor(state, entity.partnerContactUuid),
    pages: (entity.pages ?? []).map((x) => '' + x),
    author: getAuthor(
      entity.employeeUuid,
      entity.partnerContactUuid,
      currentEmployeeUuid(state),
      currentContactUuid(state),
    ),
    hasRelatedMeetingEvent:
      entity.meetingOccurrenceUuid !== undefined &&
      entity.meetingOccurrenceUuid !== null &&
      entity.meetingOccurrenceUuid !== '',
    today: LocalDate.fromString(state.today),
    isMeetingReport:
      entity.meetingOccurrenceUuid !== undefined &&
      entity.meetingOccurrenceUuid !== null &&
      entity.meetingOccurrenceUuid !== '',
    deletable: true,
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'reports', uuid, organization.organizationId);
      return command.draft as ReportViewEditItem;
    },
    currentEmployeeUuid: currentEmployeeUuid(state) ?? '',
    currentUserUuid: currentUserUuid(state),
    employees: [],
    partners: [],
    contacts: [],
    content: entity.content ?? '',
    functionsWithEmployees: functionsNotDeleted(state).map((f) => {
      return { functionUuid: f.uuid, employees: f.employees };
    }),
    isForm: entity.isForm,
    elements: [],
  };
  if (resultItem.isMeetingReport) {
    const meetingOccurrence = organization.meetingOccurrences.find((m) => m.uuid === entity.meetingOccurrenceUuid);
    if (!meetingOccurrence) {
      // Rapporten refererer til en møte-occurrence som ikke ligger i state — kan
      // skje hvis møtet er slettet, eller hvis fetchOrganizationMeetingOccurrences
      // ikke har landet enda. Rapport-innholdet er trygt å rendre uten møte-
      // metadata, så vi degraderer i stedet for å kaste og ta ned hele view'et.
      notifyError(new Error(`Report ${uuid} references missing meetingOccurrence ${entity.meetingOccurrenceUuid}`), {
        severity: 'warning',
        context: 'Report missing meetingOccurrence',
        metadata: {
          report: {
            uuid,
            meetingOccurrenceUuid: entity.meetingOccurrenceUuid,
            meetingOccurrencesInState: organization.meetingOccurrences.length,
          },
        },
      });
      resultItem.name = entity.name && entity.name !== '' ? entity.name : t('library.signatures.entityTypeReport');
      return resultItem;
    }
    resultItem.meetingOccurrence = meetingOccurrence;
    resultItem.name = t('meetings.messageDialogTitleReport', { name: meetingOccurrence.name });
    resultItem.meetingOccurrenceDisplay = dateTimeDescription(
      meetingOccurrence.date,
      meetingOccurrence.time,
      meetingOccurrence.durationMinutes,
    );
    resultItem.secondaryLabel = meetingOccurrence.name?.toLowerCase().includes('medarbeidersamtale')
      ? t('meetings.withParticipant', { name: organization.employeesById[meetingOccurrence.participants[0]].name })
      : '';
    resultItem.hasSigningModule = true;
    resultItem.employees = meetingOccurrence.participants ?? [];
    resultItem.partners = []; // TODO avhengig av endringer i dialoger.
    resultItem.contacts = meetingOccurrence.externalParticipants ?? [];
    resultItem.availableEmployees = sortedByName(employeesNotDeleted(state))
      .filter((e) => e.status !== 'TERMINATED' && e.isConfirmedEntity)
      .map((p) => ({
        uuid: p.uuid,
        email: p.email,
        personalIdentifier: p.personalIdentifier,
        firstName: p.firstName,
        lastName: p.lastName,
      }));
    resultItem.availableContacts = sortBy(contactsNotDeleted(state), 'firstName', 'lastName')
      .filter((e) => e.isConfirmedEntity)
      .map((p) => ({
        uuid: p.uuid,
        email: p.email,
        personalIdentifier: p.personalIdentifier,
        firstName: p.firstName,
        lastName: p.lastName,
      }));
    const meetingResponsibleFunction = organization.functionsById[meetingOccurrence.responsibleUuid ?? ''];
    const meetingResponsibleFunctionEmployeeUuid = meetingResponsibleFunction?.employees[0];
    resultItem.meetingParticipants = meetingParticipants(
      meetingResponsibleFunctionEmployeeUuid,
      meetingOccurrence.responsibleEmployeeUuid,
      meetingOccurrence.participants,
      meetingOccurrence.externalParticipants,
    );
    resultItem.availableMeetingMessageRecipients = availableMeetingMessageRecipients(
      employeesShortNamesAndI(state),
      employeesNotDeleted(state),
      getContactsAsUsers(state),
      resultItem.meetingParticipants,
      meetingOccurrence.externalParticipants,
      meetingOccurrence.name?.toLowerCase().includes('medarbeidersamtale') ?? false,
    );
  }
  if (resultItem.isForm) {
    if (entity.createdFromTemplate) {
      resultItem.createdFromTemplate = entity.createdFromTemplate;
      const template = suggestedReports(state).find((r) => {
        return r.pageId === entity.createdFromTemplate;
      });
      if (template) {
        resultItem.helpContent = template.helpContent;
      }
    }
    resultItem.anonymousForm = entity.anonymousForm;
    resultItem.formSubtitle = entity.formSubtitle;
    resultItem.availableFunctionsForForm = availableFunctions(state);
    resultItem.availableEmployeesForResponsible = availableEmployees(state, resultItem.formResponsibleEmployees);
    resultItem.availableEmployeesForRespondent = availableEmployees(state, resultItem.formRespondentContacts);
    resultItem.availableContactsForResponsible = availableContacts(state, resultItem.formResponsibleContacts);
    resultItem.availableContactsForRespondent = availableContacts(state, resultItem.formRespondentContacts);

    resultItem.currentUserName = currentUserName(state);

    resultItem.formResponsibleEmployees = entity.formResponsibleEmployees;
    resultItem.formResponsibleContacts = entity.formResponsibleContacts;
    resultItem.formResponsible = [];
    if (entity.formResponsibleFunction) {
      resultItem.formResponsible.push(entity.formResponsibleFunction);
    }
    if (entity.formResponsibleEmployees) {
      resultItem.formResponsible = resultItem.formResponsible.concat(entity.formResponsibleEmployees);
    }
    if (entity.formResponsibleContacts) {
      resultItem.formResponsible = resultItem.formResponsible.concat(entity.formResponsibleContacts);
    }

    resultItem.formRespondentEmployees = entity.formRespondentEmployees;
    resultItem.formRespondentContacts = entity.formRespondentContacts;
    resultItem.formRespondents = (entity.formRespondentEmployees ?? []).concat(entity.formRespondentContacts ?? []);

    resultItem.contactsForShare = getContactsForShare(
      resultItem.formResponsibleContacts ?? [],
      resultItem.formRespondentContacts ?? [],
      availableContacts(state),
      resultItem.currentUserUuid ?? '',
    );

    resultItem.formResponseDeadline = entity.formResponseDeadline ?? '';
    resultItem.content = entity.content ?? '';
    resultItem.formResponsibleFunction = entity.formResponsibleFunction;
    resultItem.userNames = userNamesAndI(state).map((n) => {
      return { value: n.value, text: capitalize(n.text) };
    });
    resultItem.userNamesForPdf = userNames(state).map((n) => {
      return { value: n.value, text: capitalize(n.text) };
    });
    resultItem.currentUserIsResponsible = isResponsible(state, resultItem);
    resultItem.currentUserIsRespondent = isRespondent(resultItem);
    resultItem.formPrompt = getFormPrompt(
      state,
      resultItem.formResponsibleFunction ?? '',
      resultItem.formResponsibleEmployees ?? [],
      resultItem.formResponsibleContacts ?? [],
      resultItem.formResponseDeadline ?? '',
    );
    resultItem.elements = (entity.formElements ?? []) as FormElement[];
    resultItem.isSurvey = extractFormData(resultItem.elements).respondentMultipleFieldsCount > 0;
  }
  return resultItem;
}
